import { watch } from 'vue';

/*
    Code which handles CATEGORY functionality
*/
export function useCategories(
    queryParams,
    categoriesQuery,
    categoriesStore,
    facetsQuery,
    facetsStore,
    paramsFromUrl,
    settings,
    useFetch,
) {
    /*
        check if a given id exists in a given array
    */
    function intIsInArray(int, array) {

        for (let i = 0; i < array.length; i++) {

            if (parseInt(int) === parseInt(array[i])) {

                return true;

            }

        }

        return false;

    }

    function pushTopLevelCategory(cat, isChecked, array) {

        const isQuickFilter = intIsInArray(cat.id, settings.quickFilters);

        array.push(
            {
                id: cat.id,
                title: cat.title,
                isEventCategory: cat.isEventCategory,
                checked: isChecked,
                filterIcon: cat.filterIcon,
                filterIconSvg: cat.filterIconSvg,
                isQuickFilter: isQuickFilter,
            }
        )

    }

    function pushChildCategory(child, isChecked, array, isEventCategory) {

        const isQuickFilter = intIsInArray(child.id, settings.quickFilters);

        array.push(
            {
                id: child.id,
                title: child.title,
                parentId: child.parentID,
                isQuickFilter: isQuickFilter,
                checked: isChecked,
                filterIcon: child.filterIcon,
                filterIconSvg: child.filterIconSvg,
                isEventCategory: isEventCategory,
            }
        )

    }

    /*
        check whether a category should be checked on page load
    */
    function isCatChecked(cat) {

        if (paramsFromUrl['categories']) {

            const chosenCategories = paramsFromUrl['categories'].split(',');

            for (let i = 0; i < chosenCategories.length; i++) {

                if (parseInt(chosenCategories[i]) === parseInt(cat.id)) {

                    return true;

                }

            }

        }

        return false;

    }

    /*
        Take json from graphql query and simplify it into
        two arrays:

        'topLevelCategoryArray', structured as follows:

        [
            {
                id: int
                title: string
                isEventCategory: bool
                checked: bool
                filterIcon: string
            },
            etc.
        ]

        'categoryArray', structured as follows:

        [
            {
                id: int
                title: string
                parentId: int
                isQuickFilter: bool
                checked: bool
                filterIcon: string
            },
            etc.
        ]


        This makes it easier to manipulate the data in the app
    */
    async function createCategoryArrays(json) {

        let topLevelCategoryArray = [];
        let categoryArray = [];

        /*
            if isEventsPage, add event category to the topLevelCategoryArray,
            and all children of the event category to the categoryArray
        */
        if (settings.isEventsPage && json.data) {

            for (let i = 0; i < json.data.categories.length; i++) {

                const cat = json.data.categories[i];

                if (cat.isEventCategory) {

                    pushTopLevelCategory(cat, isCatChecked(cat), topLevelCategoryArray);

                    const catChildren = cat.children;

                    for (let j = 0; j < catChildren.length; j++) {

                        const child = catChildren[j];

                        pushChildCategory(child, isCatChecked(child), categoryArray, cat.isEventCategory);

                        if (j === catChildren.length -1) {

                            return {
                                topLevelCategoryArray: topLevelCategoryArray,
                                categoryArray: categoryArray,
                            }

                        }

                    }

                }

            }

        }

        if (json.data) {

            for (let i = 0; i < json.data.categories.length; i++) {

                const cat = json.data.categories[i];
                let didPushParent = false;

                if (intIsInArray(cat.id, settings.categories) || !settings.categories.length) {

                    pushTopLevelCategory(cat, isCatChecked(cat), topLevelCategoryArray);

                    didPushParent = true;

                }

                const catChildren = cat.children;
                let didPushChildren = false;

                for (let j = 0; j < catChildren.length; j++) {

                    const child = catChildren[j];

                    if (intIsInArray(child.id, settings.categories)) {

                        pushChildCategory(child, isCatChecked(child), categoryArray, cat.isEventCategory);

                        didPushChildren = true;

                    }

                    /*
                        if the admin selected a topLevelCategory, but none of its children,
                        then add all of the children to the categories array!
                    */
                    if (j === catChildren.length - 1 && didPushParent && !didPushChildren) {

                        for (let k = 0; k < catChildren.length; k++) {

                            const child = catChildren[k];

                            pushChildCategory(child, isCatChecked(child), categoryArray, cat.isEventCategory);

                        }

                    }
                }

            }

        }

        return {
            topLevelCategoryArray: topLevelCategoryArray,
            categoryArray: categoryArray,
        }


    }

    /*
        Take json from graphql query and turn it into useable
        arrays (using createCategoryArrays, above).

        Then, check if any of the categories need to have 'checked = true'
        on page load, by checking against routing params

        Note that watchCategories is not watching the
        categories store at this point.
    */
    async function setInitialCategories(json) {

        let arrays = await createCategoryArrays(json);

        let chosenCatIDs = [];

        if (paramsFromUrl['topLevelCategory']) {

            categoriesStore.chosenTopLevelCategory = paramsFromUrl['topLevelCategory'];

        }

        if (paramsFromUrl['categories']) {

            chosenCatIDs = paramsFromUrl['categories'].split(',').map((id) => {
                return parseInt(id);
            });

        }

        for (let i = 0; i < chosenCatIDs.length; i++) {

            const id = chosenCatIDs[i];

            /*
                If the chosen category is not already in the 'categories' query param,
                then add it to the 'categories' query param
            */
            if (queryParams.params.categories.indexOf(id) === -1) {

                queryParams.pushToParamArray('categories', id);

            }

        }

        /*
            If the admin has not selected any top-level categories,
        */
        if (arrays.topLevelCategoryArray.length === 0) {

            // set initial value of showAll
            categoriesStore.setShowAll(true);

            // uncheck all categories and add all categories to our param array
            for (let i = 0; i < arrays.categoryArray.length; i++) {

                arrays.categoryArray[i].checked = false;

                // Initial category query param is handled by useParams.getDefaultParams()

            }

        }

        // Determine whether to show category filters
        if (arrays.topLevelCategoryArray.length > 1 || arrays.categoryArray.length > 1) {

            categoriesStore.setShowCategoryFilters(true);

        }

        categoriesStore.updateTopLevelCategories(arrays.topLevelCategoryArray);
        categoriesStore.updateCategories(arrays.categoryArray);

    }

    /*
        Initial setup: this undertakes the initial graphql query
        for categories and sends the resulting json to the
        above functions
    */
    async function getCategories() {

        useFetch({
            url: settings['host'],
            apiKey: settings['key'],
            query: categoriesQuery({
                allCategories: true,
                filterIconExtraClasses: settings['categorySvgClasses'] ?? '',
            }),
            callback: setInitialCategories,
        });

    };

    /*
        every time the categories store changes (which will happen if we
        check / uncheck a category):
            1. reset the 'categories' query param!
            2. reset the 'startDate' and 'endDate' query params!
            3. update the 'chosenTopLevelCategory' and 'categories'
                query params
            4. grab the correct facets! (we need to show appropriate
                facets for the chosen categories)

        Note that we are not watching the categories store during initial setup
    */
    function watchCategories() {

        /*
            every time the categories store changes:
                1. reset the 'categories' query param!
                2. reset the 'startDate' and 'endDate' query params!
                3. grab the correct facets!
        */
        categoriesStore.$subscribe(() => {

            queryParams.resetParamArray(
                'categories',
            );

            const showAll = categoriesStore.showAll;
            const topCats = categoriesStore.topLevelCategories;
            const cats = categoriesStore.categories;

            /**
             * Are any categories checked?
             * @returns {boolean}
             */
            const anyChosenCategories = cats.some(cat => cat.checked);

            if (anyChosenCategories) {

                /*
                    push checked categories to our param array
                */
                for (let i = 0; i < cats.length; i++) {

                    if (cats[i].checked) {

                        if (!categoriesStore.chosenTopLevelCategory) {

                            queryParams.pushToParamArray('categories', parseInt(cats[i].id));

                        } else if (categoriesStore.chosenTopLevelCategory === cats[i].parentId) {

                            queryParams.pushToParamArray('categories', parseInt(cats[i].id));

                        }

                    }

                }

                categoriesStore.setShowAll(false);

                return;

            }

            if (showAll) {

                /*
                    if "showAll" === true,
                    uncheck all categories
                */

                for (let i = 0; i < cats.length; i++) {

                    cats[i].checked = false;

                }

            }

            /** @var array Categories to get facets for */
            const facetCategories = [];

            if (categoriesStore.chosenTopLevelCategory) {

                facetCategories.push(categoriesStore.chosenTopLevelCategory);
                queryParams.pushToParamArray('categories', categoriesStore.chosenTopLevelCategory);

            } else {

                for (let i = 0; i < topCats.length; i++) {

                    facetCategories.push(topCats[i].id);

                }

            }

            useFetch({
                url: settings['host'],
                apiKey: settings['key'],
                query: facetsQuery({categoryIds: facetCategories}),
                callback: facetsStore.updateFacets,
            });

        });

    }

    return {
        getCategories,
        watchCategories,
    }

}
